import React from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

/**
 * Chart data format expected in ```chart code blocks.
 *
 * The backend should return JSON like:
 * {
 *   "type": "line" | "bar" | "area",
 *   "title": "Monthly CCV Comparison",
 *   "xKey": "month",
 *   "series": [
 *     { "key": "2024", "label": "2024", "color": "#8884d8" },
 *     { "key": "2025", "label": "2025", "color": "#82ca9d" }
 *   ],
 *   "data": [
 *     { "month": "Jan", "2024": 1200000, "2025": 1450000 },
 *     { "month": "Feb", "2024": 1100000, "2025": 1380000 },
 *     ...
 *   ]
 * }
 */

interface ChartSeries {
  key: string;
  label?: string;
  color?: string;
}

interface ChartData {
  type?: 'line' | 'bar' | 'area';
  title?: string;
  xKey: string;
  yLabel?: string;
  series: ChartSeries[];
  data: Record<string, unknown>[];
}

const DEFAULT_COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe',
  '#00c49f', '#ffbb28', '#ff8042', '#a4de6c', '#d0ed57',
];

/** Formats large numbers with K/M/B suffixes for axis labels. */
function formatAxisValue(value: unknown): string {
  if (typeof value !== 'number') return String(value);
  if (Math.abs(value) >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

/** Formats tooltip values with full number formatting. */
function formatTooltipValue(value: unknown): string {
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
}

/**
 * Renders a chart from structured JSON data.
 * Supports line, bar, and area chart types with multiple series.
 */
export function ChartBlock({ content }: { content: string }) {
  let chartData: ChartData;
  try {
    // Strip common LLM artifacts: trailing commas before } or ], markdown wrapping
    const cleaned = content
      .replace(/```(chart|json)?\n?/g, '')
      .replace(/,\s*([\]}])/g, '$1')
      .trim();
    chartData = JSON.parse(cleaned);
  } catch {
    return (
      <div style={{ padding: '12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '13px', color: '#856404' }}>
        ⚠️ Could not parse chart data. Expected valid JSON.
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer' }}>Show raw data</summary>
          <pre style={{ fontSize: '11px', whiteSpace: 'pre-wrap', marginTop: '4px' }}>{content.slice(0, 500)}</pre>
        </details>
      </div>
    );
  }

  if (!chartData.data || !chartData.series || !chartData.xKey) {
    return (
      <div style={{ padding: '12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', fontSize: '13px', color: '#856404' }}>
        ⚠️ Chart data is missing required fields (data, series, xKey).
      </div>
    );
  }

  // Coerce string-encoded numbers (e.g., "5.2%" or "1,200") to actual numbers
  const numericData = chartData.data.map((row) => {
    const cleaned: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(row)) {
      if (typeof val === 'string' && key !== chartData.xKey) {
        const num = parseFloat(val.replace(/[,%]/g, ''));
        cleaned[key] = isNaN(num) ? val : num;
      } else {
        cleaned[key] = val;
      }
    }
    return cleaned;
  });

  const { type = 'line', title, xKey, yLabel, series } = chartData;

  const commonProps = {
    data: numericData,
    margin: { top: 10, right: 30, left: 20, bottom: 5 },
  };

  const renderSeries = () =>
    series.map((s, idx) => {
      const color = s.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length];
      const name = s.label || s.key;

      switch (type) {
        case 'bar':
          return <Bar key={s.key} dataKey={s.key} name={name} fill={color} />;
        case 'area':
          return (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={name}
              stroke={color}
              fill={color}
              fillOpacity={0.3}
            />
          );
        case 'line':
        default:
          return (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={name}
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          );
      }
    });

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatAxisValue} tick={{ fontSize: 12 }} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', style: { fontSize: 12 } } : undefined} />
            <Tooltip formatter={formatTooltipValue} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            {renderSeries()}
          </BarChart>
        );
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatAxisValue} tick={{ fontSize: 12 }} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', style: { fontSize: 12 } } : undefined} />
            <Tooltip formatter={formatTooltipValue} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            {renderSeries()}
          </AreaChart>
        );
      case 'line':
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={formatAxisValue} tick={{ fontSize: 12 }} label={yLabel ? { value: yLabel, angle: -90, position: 'insideLeft', style: { fontSize: 12 } } : undefined} />
            <Tooltip formatter={formatTooltipValue} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            {renderSeries()}
          </LineChart>
        );
    }
  };

  return (
    <div style={{ margin: '8px 0', padding: '16px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
      {title && (
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: '#333', textAlign: 'center' }}>
          📊 {title}
        </div>
      )}
      <ResponsiveContainer width="100%" height={300}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
}
